import * as React from "react";
import { Helmet } from "react-helmet";
import Loadable from "react-loadable";
import { Redirect } from "react-router-dom";
import styled from "styled-components";
import { useLocation } from "react-router";

import {
  PAYMENT_WAITING_TIMEOUT,
  PAYMENT_WAITING_TIMEOUT_DEV,
  FETCH_PAYMENT_STATUS_INTERVAL,
  IS_INTERNAL_ENV,
} from "@config/consts";
import { ROUTES } from "@config/app.routing";

import { PAYMENT_STATUS } from "@typings/payments";

import { Loader, Paragraph } from "@ui/Atoms";
import { Container } from "@ui/Atoms/Grid";

import { extractQueries } from "@services/QueryParamsParser";
import { PAYMENT_STATUS_RETURN_TYPE } from "@services/ProvidePaymentUrls";
import { safelyAddQuery } from "@services/SafelyAddQuery";
import { buildUrl } from "@services/BuildUrl";
import { getSearchParamsFromURL } from "@services/GetSearchParamsFromURL/GetSearchParamsFromURL";
import { AsyncActionResult } from "@services/AsyncActionCreatorFactory";

import { TypeOptions } from "@containers/Toastify";

import { useVoucherManager } from "@hooks/useVoucherManager";
import { SendEvent } from "@hooks/useTelemetry";

import { PageSkeleton } from "@app/pages/EndOfTenancy/EndOfTenancy.skeletons";

/**
 * This check is needed for payments that come back from 3rd party payment providers as success,
 * but then fail at our backend payment status validation.
 * First such case was Twint.
 * More at: https://docs.google.com/presentation/d/1Bjc7WwjwbCdqtPcJ0sUQ8ROQFx7FtlehWWoSPyjPYsk
 */
const ensureCorrectErrorParams = (route: string, shouldHaveError: boolean) => {
  if (shouldHaveError) {
    return route.includes(`paymentStatus=${PAYMENT_STATUS_RETURN_TYPE.ERROR}`)
      ? route
      : safelyAddQuery(
          route,
          `paymentStatus=${PAYMENT_STATUS_RETURN_TYPE.ERROR}`,
        );
  }

  return route;
};

interface RedirectProps {
  route: string;
  publicMessage: string | null;
  toastType: TypeOptions;
  showMessageToast: (message: string | null, type: TypeOptions) => void;
}

const redirect = ({
  route,
  publicMessage,
  toastType,
  showMessageToast,
}: RedirectProps) => {
  // always show toast when there is an error
  // publicMessage will fallback to frontend translation key
  if (toastType === "error") {
    showMessageToast(publicMessage, toastType);
  }

  // only show success toast if backend returns message
  if (toastType === "success" && publicMessage) {
    showMessageToast(publicMessage, toastType);
  }

  const preparedRoute = ensureCorrectErrorParams(
    decodeURIComponent(route),
    toastType === "error",
  );

  return <Redirect to={preparedRoute} />;
};

const TIMEOUT = IS_INTERNAL_ENV
  ? PAYMENT_WAITING_TIMEOUT_DEV
  : PAYMENT_WAITING_TIMEOUT;

interface Props {
  paymentStatus: PAYMENT_STATUS;
  publicMessage: string | null;
  originRoute: string;
  targetRoute: string;
  fetchPaymentStatus: () => Promise<AsyncActionResult>;
  setReferenceNumber: (referenceNumber: string) => void;
  setOriginRoute: (originRoute: string, serviceLocationId: string) => void;
  setTargetRoute: (targetRoute: string) => void;
  showMessageToast: (message: string | null, type: TypeOptions) => void;
  sendEvent?: SendEvent;
  htmlMetaTitleText: string;
  htmlMetaDescriptionText: string;
}

const LoadableFakePayment = Loadable({
  loader: () => import("./FakePayment"),
  render(loaded: any, props: any): JSX.Element {
    const Component = loaded.FakePayment;
    return <Component {...props} />;
  },
  loading: PageSkeleton,
});

const PaymentProcessed = (props: Props): React.ReactElement => {
  const location = useLocation();
  const voucherManager = useVoucherManager();
  const [waitingTime, setWaitingTime] = React.useState<number>(0);
  const [eventIsSent, setEventIsSent] = React.useState(false);
  const queryParams = extractQueries(location.search);

  // Remove ?datatransTrxId=XXX from URL
  React.useEffect(() => {
    const datatransTrxId = getSearchParamsFromURL(window.location.href).get(
      "datatransTrxId",
    );

    if (!datatransTrxId) return;

    window.history.replaceState(
      null,
      "",
      window.location.href.replace(`?datatransTrxId=${datatransTrxId}`, ""),
    );
  }, []);

  const targetRoute = buildUrl(props.targetRoute).addQuery(
    "contractUuid",
    queryParams?.contractUuid ?? "",
  );

  if (props.targetRoute.includes(ROUTES.URSSAF_REGISTRATION)) {
    targetRoute.addQuery("bookingCompleted", "true");
  }

  const getRedirectProps = (
    toastType: RedirectProps["toastType"],
  ): RedirectProps => ({
    route: toastType === "error" ? props.originRoute : targetRoute.done(),
    publicMessage: props.publicMessage,
    showMessageToast: props.showMessageToast,
    toastType,
  });

  const handleEvent = async (eventName: string) => {
    if (eventIsSent) return;

    await props.sendEvent?.(eventName);
    setEventIsSent(true);
  };

  React.useEffect(() => {
    const interval = setInterval(() => {
      setWaitingTime(waitingTime => waitingTime + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  React.useEffect(() => {
    if (queryParams) {
      props.setReferenceNumber(queryParams.referenceNumber);
      props.setOriginRoute(
        queryParams.originRoute,
        queryParams.serviceLocationId,
      );
      props.setTargetRoute(queryParams.targetRoute);
    }
    const fetchPaymentStatusInterval = setInterval(() => {
      (async () => {
        await props.fetchPaymentStatus();
      })();
    }, FETCH_PAYMENT_STATUS_INTERVAL);

    return () => clearInterval(fetchPaymentStatusInterval);
  }, []);

  // *** CANCELLED BY USER ***
  // Payment was intentionally cancelled by user from UI.
  // We are manually redirecting user back to originRoute, because API is unable to validate `paymentStatus`.
  if (
    props.paymentStatus === PAYMENT_STATUS.PENDING &&
    queryParams?.status === PAYMENT_STATUS_RETURN_TYPE.CANCEL.toLowerCase() &&
    props.originRoute
  ) {
    handleEvent("payment_cancelled");

    return (
      <React.Fragment>
        {eventIsSent && redirect(getRedirectProps("error"))}
      </React.Fragment>
    );
  }

  // *** AUTHORIZED/PAID ***
  if (
    (props.paymentStatus === PAYMENT_STATUS.PAID ||
      props.paymentStatus === PAYMENT_STATUS.AUTHORIZED) &&
    props.targetRoute
  ) {
    voucherManager.removeStoredVoucher();
    voucherManager.removeStoredDeeplinkVoucher();

    handleEvent("payment_done");

    return (
      <React.Fragment>
        {eventIsSent && redirect(getRedirectProps("success"))}
      </React.Fragment>
    );
  }

  // *** CANCELLED/FAILED/REFUSED ***
  if (
    (props.paymentStatus === PAYMENT_STATUS.CANCELLED ||
      props.paymentStatus === PAYMENT_STATUS.FAILED ||
      props.paymentStatus === PAYMENT_STATUS.REFUSED) &&
    props.originRoute
  ) {
    handleEvent("payment_failed");

    return (
      <React.Fragment>
        {eventIsSent && redirect(getRedirectProps("error"))}
      </React.Fragment>
    );
  }

  // *** PAYMENT_WAITING_TIMEOUT ***
  if (waitingTime > TIMEOUT) {
    handleEvent("payment_timeout");

    return (
      <React.Fragment>
        {eventIsSent && redirect(getRedirectProps("error"))}
      </React.Fragment>
    );
  }

  // *** PENDING ***
  return (
    <Container>
      <Helmet>
        <title>{props.htmlMetaTitleText}</title>
      </Helmet>
      <PaymentProcessedBody>
        <Loader />
        <ParagraphStyled>{props.htmlMetaDescriptionText}</ParagraphStyled>
        {IS_INTERNAL_ENV && <LoadableFakePayment />}
      </PaymentProcessedBody>
    </Container>
  );
};

export { PaymentProcessed };

const PaymentProcessedBody = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  padding: ${props => props.theme.margins.base_x6} 0;
  transform: translateY(50%);
`;

const ParagraphStyled = styled(Paragraph)`
  margin-top: ${props => props.theme.margins.base_x2};
`;
